import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthService } from '../services/auth.service'
import { supabase } from '../services/supabase.client'

interface User {
  id: string
  email: string
  fullName: string
  avatarUrl?: string
}

interface AuthState {
  user: User | null
  hasAccess: boolean
  isLoading: boolean
  isInitialized: boolean
  initialize: () => Promise<void>
  signInWithGoogle: () => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  clearAuth: () => void
}

let authListenerSetup = false

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      hasAccess: false,
      isLoading: false,
      isInitialized: false,

      initialize: async () => {
        try {
          const session = await AuthService.getSession()

          if (!session?.user) {
            set({
              user: null,
              hasAccess: false,
              isInitialized: true
            })
            return
          }

          const permissions = await AuthService.checkPermissions(session.user.id)

          if (!permissions.hasAccess) {
            set({
              user: {
                id: session.user.id,
                email: session.user.email!,
                fullName: session.user.user_metadata.full_name || session.user.email!,
                avatarUrl: session.user.user_metadata.avatar_url
              },
              hasAccess: false,
              isInitialized: true
            })
            return
          }

          const profile = await AuthService.getUserProfile(session.user.id)

          set({
            user: {
              id: session.user.id,
              email: profile?.email || session.user.email!,
              fullName: profile?.full_name || session.user.user_metadata.full_name || session.user.email!,
              avatarUrl: profile?.avatar_url || session.user.user_metadata.avatar_url
            },
            hasAccess: true,
            isInitialized: true
          })

          if (!authListenerSetup) {
            authListenerSetup = true
            supabase.auth.onAuthStateChange(async (event, session) => {
              if (event === 'SIGNED_OUT') {
                get().clearAuth()
              } else if (event === 'SIGNED_IN' && session && !get().user) {
                await get().initialize()
              }
            })
          }
        } catch (error) {
          console.error('[AuthStore] Error initializing auth:', error)
          set({
            user: null,
            hasAccess: false,
            isInitialized: true
          })
        }
      },

      signInWithGoogle: async () => {
        try {
          set({ isLoading: true })

          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
              queryParams: {
                hd: 'excelschools.edu.in',
                prompt: 'select_account',
              },
              redirectTo: `${window.location.origin}/auth/callback`,
            },
          })

          if (error) throw error

          return { error: null }
        } catch (error) {
          return { error: error as Error }
        } finally {
          set({ isLoading: false })
        }
      },

      signOut: async () => {
        await AuthService.signOut()
        get().clearAuth()
      },

      clearAuth: () => {
        set({
          user: null,
          hasAccess: false,
          isLoading: false
        })
      }
    }),
    {
      name: 'qr-auth-storage',
      partialize: (state) => ({
        user: state.user,
        hasAccess: state.hasAccess
      })
    }
  )
)
